/**
 * ChurnExitFormModal.jsx — Iter 171.A (Danny — Churned/Exited Phase A)
 *
 * Manager-tier modal that captures the spec's required exit form
 * fields and POSTs `/api/customers/{id}/churn`. Closes on success and
 * fires `onSubmitted()` so the parent page can re-fetch.
 *
 * Required fields:
 *   - sub_type (one of 7)
 *   - date (ISO)
 *   - revenue_at_exit (auto-populated from `defaultRevenue`, editable)
 *
 * Conditional fields:
 *   - acquired_by (when sub_type === sold_acquired) — at least
 *     `entity_name` is required
 *   - switched_to_competitor (when sub_type === churned_competitor)
 *
 * Optional:
 *   - notes (free-text up to 1000 chars)
 *
 * Sub-types come from `GET /api/customers/exit-sub-types` so we don't
 * duplicate the spec list on the client.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Archive, X, Loader2 } from "lucide-react";

// Iter 171.A — spec sub-types inlined here so the modal doesn't need an
// effect-driven fetch. Backend `/api/customers/exit-sub-types` still
// exists for validation + future analytics consumers. Keep this list
// in sync with `EXIT_SUB_TYPES` in `backend/routes/customers_routes.py`.
const EXIT_SUB_TYPES = [
  { key: "closed_shutdown",      label: "Closed — Practice shut down",         re_engage: "never",         tone: "gray"  },
  { key: "closed_retired",       label: "Closed — Physician retired",          re_engage: "manual",        tone: "gray"  },
  { key: "sold_acquired",        label: "Sold — Practice acquired",            re_engage: "immediate",     tone: "amber" },
  { key: "churned_competitor",   label: "Churned — Switched to competitor",    re_engage: "6_months",      tone: "red"   },
  { key: "churned_non_payment",  label: "Churned — Non-payment / Collections", re_engage: "finance_hold",  tone: "red"   },
  { key: "churned_dissatisfied", label: "Churned — Service dissatisfied",      re_engage: "90_days",       tone: "red"   },
  { key: "churned_unresponsive", label: "Churned — Unknown / Unresponsive",    re_engage: "90_days",       tone: "red"   },
];

export default function ChurnExitFormModal({ open, onClose, customerId, customerName, apiBase, token, defaultRevenue = 0, onSubmitted }) {
  const subTypes = EXIT_SUB_TYPES;
  const [submitting, setSubmitting] = useState(false);

  const [subType, setSubType] = useState("");
  const [exitDate, setExitDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [revenue, setRevenue] = useState(() => defaultRevenue || 0);
  const [notes, setNotes] = useState("");
  const [acquiredEntity, setAcquiredEntity] = useState("");
  const [acquiredFirst, setAcquiredFirst] = useState("");
  const [acquiredLast, setAcquiredLast] = useState("");
  const [acquiredEmail, setAcquiredEmail] = useState("");
  const [acquiredPhone, setAcquiredPhone] = useState("");
  const [competitor, setCompetitor] = useState("");

  // Modal returns null while closed, so component state is fresh on every
  // open — no reset effect needed. (Lazy useState above seeds `revenue`
  // from `defaultRevenue` on mount.)

  if (!open) return null;

  const selectedRule = subTypes.find(s => s.key === subType);
  const needsAcquiredBy = subType === "sold_acquired";
  const needsCompetitor = subType === "churned_competitor";

  const canSubmit = !!subType && !!exitDate && Number(revenue) >= 0 &&
    (!needsAcquiredBy || acquiredEntity.trim().length > 0);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const body = {
        sub_type: subType,
        date: exitDate,
        revenue_at_exit: Number(revenue) || 0,
        notes: notes.trim() || null,
      };
      if (needsAcquiredBy) {
        body.acquired_by = {
          entity_name: acquiredEntity.trim(),
          contact_first_name: acquiredFirst.trim() || null,
          contact_last_name: acquiredLast.trim() || null,
          contact_email: acquiredEmail.trim() || null,
          contact_phone: acquiredPhone.trim() || null,
        };
      }
      if (needsCompetitor) {
        body.switched_to_competitor = competitor.trim() || null;
      }
      const r = await axios.post(`${apiBase}/customers/${customerId}/churn`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Moved ${customerName || "customer"} to Churned / Exited`);
      onSubmitted?.(r.data);
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't save";
      toast.error(typeof msg === "string" ? msg : "Couldn't save");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="churn-exit-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", backdropFilter: "blur(4px)", zIndex: 9100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={() => onClose?.()}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 640, maxHeight: "90vh", overflow: "auto", boxShadow: "0 22px 60px -12px rgba(15,23,42,0.45)" }}>
        <div style={{ padding: "16px 22px", borderBottom: "0.5px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Archive style={{ width: 16, height: 16, color: "#475569" }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Move customer to Churned / Exited</div>
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{customerName || "Customer"} — record stays in the CRM; all enrolled products will be marked terminated on the exit date.</div>
            </div>
          </div>
          <button onClick={() => onClose?.()} data-testid="churn-exit-close" style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "#64748b" }}>
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <div style={{ padding: 22, display: "flex", flexDirection: "column", gap: 16 }}>
          <Field label="Exit sub-type (required)">
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 4 }}>
              {subTypes.map(s => (
                  <button
                    key={s.key}
                    type="button"
                    data-testid={`exit-sub-type-${s.key}`}
                    onClick={() => setSubType(s.key)}
                    style={{
                      textAlign: "left", padding: "10px 12px", borderRadius: 8,
                      background: subType === s.key ? toneSoftBg(s.tone) : "#fff",
                      border: subType === s.key ? `1px solid ${toneBorder(s.tone)}` : "0.5px solid var(--border)",
                      cursor: "pointer", fontFamily: "inherit",
                    }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: subType === s.key ? toneText(s.tone) : "var(--text)" }}>{s.label}</span>
                      <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 10, background: toneSoftBg(s.tone), color: toneText(s.tone), textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        {humanRule(s.re_engage)}
                      </span>
                    </div>
                  </button>
                ))}
            </div>
          </Field>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Exit date (required)">
              <input
                data-testid="exit-date-input"
                type="date"
                value={exitDate}
                onChange={(e) => setExitDate(e.target.value)}
                className="input" style={{ fontSize: 13 }} />
            </Field>
            <Field label="Revenue at exit (required)">
              <input
                data-testid="exit-revenue-input"
                type="number" min="0" step="50"
                value={revenue}
                onChange={(e) => setRevenue(e.target.value)}
                className="input num" style={{ fontSize: 13 }} />
              <div style={{ fontSize: 10, color: "var(--text-3)", marginTop: 4 }}>
                Auto-populated from active enrollments. Edit if final MRR differs.
              </div>
            </Field>
          </div>

          {needsAcquiredBy && (
            <div data-testid="exit-acquired-by-block" style={{ background: "#FFFBEB", borderRadius: 10, padding: 14, border: "0.5px solid #FCD34D" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#92400E", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 8 }}>Acquiring entity — creates a warm lead on save</div>
              <Field label="Entity name (required)">
                <input
                  data-testid="exit-acquired-entity"
                  value={acquiredEntity} onChange={(e) => setAcquiredEntity(e.target.value)}
                  placeholder="e.g. Northeast Cardiology Group" className="input" style={{ fontSize: 13 }} />
              </Field>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <Field label="Contact first name">
                  <input data-testid="exit-acquired-first" value={acquiredFirst} onChange={(e) => setAcquiredFirst(e.target.value)} className="input" style={{ fontSize: 13 }} />
                </Field>
                <Field label="Contact last name">
                  <input data-testid="exit-acquired-last" value={acquiredLast} onChange={(e) => setAcquiredLast(e.target.value)} className="input" style={{ fontSize: 13 }} />
                </Field>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <Field label="Contact email">
                  <input data-testid="exit-acquired-email" value={acquiredEmail} onChange={(e) => setAcquiredEmail(e.target.value)} className="input" style={{ fontSize: 13 }} placeholder="anna@example.com" />
                </Field>
                <Field label="Contact phone">
                  <input data-testid="exit-acquired-phone" value={acquiredPhone} onChange={(e) => setAcquiredPhone(e.target.value)} className="input" style={{ fontSize: 13 }} placeholder="+1 555 000 0000" />
                </Field>
              </div>
            </div>
          )}

          {needsCompetitor && (
            <Field label="Switched to competitor (fed into churn analytics)">
              <input
                data-testid="exit-competitor-input"
                value={competitor} onChange={(e) => setCompetitor(e.target.value)}
                placeholder="e.g. Athena, eCW, Epic"
                className="input" style={{ fontSize: 13 }} />
            </Field>
          )}

          <Field label="Notes (optional)">
            <textarea
              data-testid="exit-notes-input"
              value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Reason, conversation details, anything Finance / Sales Manager should see"
              className="input" style={{ fontSize: 12, resize: "vertical", fontFamily: "inherit" }} />
          </Field>

          {selectedRule && (
            <div style={{ fontSize: 11, color: "var(--text-2)", background: "var(--paper)", border: "0.5px dashed var(--border-soft)", borderRadius: 8, padding: 10 }} data-testid="exit-re-engage-rule">
              <strong>Re-engagement rule:</strong> {humanRuleVerbose(selectedRule.re_engage)}
            </div>
          )}
        </div>

        <div style={{ padding: "14px 22px", borderTop: "0.5px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button data-testid="churn-exit-cancel" onClick={() => onClose?.()} className="btn" style={{ background: "#fff", color: "#475569", border: "0.5px solid #e2e8f0" }}>Cancel</button>
          <button
            data-testid="churn-exit-submit"
            onClick={submit}
            disabled={!canSubmit || submitting}
            className="btn btn-primary"
            style={{ opacity: !canSubmit ? 0.5 : 1, background: "#334155", borderColor: "#334155" }}>
            {submitting ? <Loader2 style={{ width: 13, height: 13, animation: "spin 1s linear infinite" }} /> : <Archive style={{ width: 13, height: 13 }} />}
            Move to Churned / Exited
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

function toneSoftBg(tone) {
  if (tone === "red") return "#FEF2F2";
  if (tone === "amber") return "#FFFBEB";
  return "#F1F5F9";
}
function toneBorder(tone) {
  if (tone === "red") return "#FCA5A5";
  if (tone === "amber") return "#F59E0B";
  return "#94A3B8";
}
function toneText(tone) {
  if (tone === "red") return "#991B1B";
  if (tone === "amber") return "#92400E";
  return "#334155";
}
function humanRule(rule) {
  if (rule === "never") return "Never";
  if (rule === "immediate") return "Now";
  if (rule === "90_days") return "90d";
  if (rule === "6_months") return "6mo";
  if (rule === "finance_hold") return "Finance hold";
  if (rule === "manual") return "Manual";
  return rule || "";
}
function humanRuleVerbose(rule) {
  if (rule === "never") return "Do not contact — practice permanently closed.";
  if (rule === "immediate") return "Acquiring entity is a warm lead from day one — auto-created on save.";
  if (rule === "90_days") return "Re-engagement opens 90 days after exit; one outreach attempt.";
  if (rule === "6_months") return "Re-engagement window opens 6 months after exit.";
  if (rule === "finance_hold") return "Finance must clear the outstanding balance before re-engagement is unlocked.";
  if (rule === "manual") return "Eligible only if a new physician takes over the practice — manual check.";
  return "";
}
